import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabase';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useGnomeContext } from '../../context/GnomeContext';
import { FiCheckCircle, FiClock, FiLoader, FiAlertCircle, FiCalendar, FiTrash2 } from 'react-icons/fi';
import { format } from 'date-fns';
import { toast } from 'sonner';

const MobileTaskBoard = () => {
    const { activeWorkspace } = useWorkspace();
    const { markGnomeNotificationsAsRead } = useGnomeContext();
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('pending'); // 'pending' | 'completed'

    useEffect(() => {
        if (activeWorkspace) {
            fetchTasks();
            markGnomeNotificationsAsRead();

            // Subscribe for real-time updates
            const channel = supabase
                .channel(`mobile-tasks-${activeWorkspace.id}`)
                .on('postgres_changes', {
                    event: '*',
                    schema: 'public',
                    table: 'gnome_tasks',
                    filter: `workspace_id=eq.${activeWorkspace.id}`
                }, (payload) => {
                    if (payload.eventType === 'DELETE') {
                        setTasks(prev => prev.filter(t => t.id !== payload.old.id));
                    } else {
                        fetchTasks();
                    }
                })
                .subscribe();

            return () => {
                supabase.removeChannel(channel);
            };
        }
    }, [activeWorkspace, filter, markGnomeNotificationsAsRead]);

    const fetchTasks = async () => {
        setLoading(true);
        try {
            let query = supabase
                .from('gnome_tasks')
                .select('*')
                .eq('workspace_id', activeWorkspace.id)
                .order('due_at', { ascending: true });

            if (filter === 'pending') {
                query = query.neq('status', 'completed');
            } else {
                query = query.eq('status', 'completed');
            }

            const { data, error } = await query;
            if (error) throw error;
            setTasks(data || []);
        } catch (e) {
            console.error("Error fetching tasks:", e);
        } finally {
            setLoading(false);
        }
    };

    const handleComplete = async (task) => {
        try {
            // Optimistic update
            setTasks(prev => prev.filter(t => t.id !== task.id));
            toast.success("Task marked complete!");

            const { error } = await supabase
                .from('gnome_tasks')
                .update({ status: 'completed', completed_at: new Date() })
                .eq('id', task.id);

            if (error) throw error;

            // Clear any notifications tied to this gnome's task queue so the badge disappears
            if (task.gnome_id) {
                markGnomeNotificationsAsRead(task.gnome_id);
            }
        } catch (e) {
            console.error("Completion failed:", e);
            toast.error("Failed to complete task.");
            fetchTasks(); // Revert
        }
    };

    const handleDeleteTask = async (task) => {
        if (!window.confirm("Are you sure you want to delete this task?")) return;

        try {
            const { error } = await supabase
                .from('gnome_tasks')
                .delete()
                .eq('id', task.id);

            if (error) throw error;

            setTasks(prev => prev.filter(t => t.id !== task.id));
            toast.success("Task deleted successfully!");
        } catch (e) {
            console.error("Deletion failed:", e);
            toast.error("Failed to delete task.");
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'completed': return <FiCheckCircle className="text-green-500" />;
            case 'in_progress': return <FiLoader className="text-blue-500 animate-spin-slow" />;
            case 'failed': return <FiAlertCircle className="text-red-500" />;
            default: return <FiClock className="text-charcoal-500" />;
        }
    };

    return (
        <div className="flex flex-col min-h-full bg-black pt-[env(safe-area-inset-top)]">
            <div className="px-6 py-6 pb-4 bg-black shadow-sm border-b border-charcoal-800 sticky top-0 z-10">
                <h1 className="text-2xl font-bold text-white">Your Tasks</h1>

                {/* Filter Tabs */}
                <div className="flex mt-4 p-1 bg-charcoal-800 rounded-lg border border-charcoal-700">
                    <button
                        onClick={() => setFilter('pending')}
                        className={`flex-1 py-1.5 text-xs font-semibold rounded-md transition-all ${filter === 'pending' ? 'bg-black shadow text-white border border-charcoal-600' : 'text-charcoal-400 hover:text-charcoal-300'}`}
                    >
                        Pending
                    </button>
                    <button
                        onClick={() => setFilter('completed')}
                        className={`flex-1 py-1.5 text-xs font-semibold rounded-md transition-all ${filter === 'completed' ? 'bg-black shadow text-white border border-charcoal-600' : 'text-charcoal-400 hover:text-charcoal-300'}`}
                    >
                        Completed
                    </button>
                </div>
            </div>

            <div className="p-4 space-y-3 pb-24">
                {loading ? (
                    <div className="flex justify-center py-10">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-charcoal-600"></div>
                    </div>
                ) : tasks.length === 0 ? (
                    <div className="text-center py-20 opacity-50">
                        <div className="mb-4 text-4xl">🎉</div>
                        <p className="text-charcoal-500">No {filter} tasks found.</p>
                    </div>
                ) : (
                    tasks.map(task => (
                        <div key={task.id} className="bg-charcoal-800 p-4 rounded-xl border border-charcoal-700 shadow-sm flex flex-col gap-2">
                            <div className="flex items-start justify-between">
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wide ${
                                    task.priority === 'high' ? 'bg-red-500/20 text-red-400' : 'bg-blue-500/20 text-blue-400'
                                }`}>
                                    {task.priority || 'Normal'}
                                </span>
                                {task.due_at && (
                                    <span className="text-[10px] text-charcoal-400 flex items-center gap-1">
                                        <FiCalendar size={10} />
                                        {format(new Date(task.due_at), 'MMM d')}
                                    </span>
                                )}
                            </div>

                            <h3 className="text-sm font-semibold text-charcoal-100 line-clamp-2 leading-snug">
                                {task.task_description}
                            </h3>

                            <div className="flex items-center justify-between mt-2 pt-2 border-t border-charcoal-700">
                                <div className="flex items-center gap-2 text-xs text-charcoal-400">
                                    {getStatusIcon(task.status)}
                                    <span className="capitalize">{task.status.replace('_', ' ')}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => handleDeleteTask(task)}
                                        className="text-xs bg-charcoal-800 text-red-400 hover:text-red-300 px-2 py-1.5 rounded-lg active:scale-95 transition-all flex items-center gap-1 border border-charcoal-700"
                                        title="Delete task"
                                    >
                                        <FiTrash2 /> Delete
                                    </button>
                                    {filter === 'pending' && (
                                        <button
                                            onClick={() => handleComplete(task)}
                                            className="text-xs bg-charcoal-700 text-white px-3 py-1.5 rounded-lg hover:bg-charcoal-600 active:scale-95 transition-all flex items-center gap-1 border border-charcoal-600"
                                        >
                                            <FiCheckCircle /> Complete
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default MobileTaskBoard;
